import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InstallmentsTable from '../../pages/BillingAndCollection/Installments/InstallmentsTable';
import DueDateExtensionTable from '../../pages/BillingAndCollection/DueDateExtentsion/DueDateExtensionTable';
import Newconnectiontable from '../../pages/GeneralServices/NewConnection/NewConnectionTable';
import { convertDatewithTime } from '../../services/Functions';
import { getApplicationProgressStatus } from '../../utils/applicationProgressStatus';
import ChangeLoadTable from '../../pages/GeneralServices/ChangeOfLoad/ChangeOfLoadTable';
import ChangeAttributesTable from '../../pages/GeneralServices/ChangeOfAttribute/ChangeAttributesTable';
import ChangeTariffTable from '../../pages/GeneralServices/ChangeOfTariff/ChangeOfTariffTable';
import MeterChangeRequestTable from '../../pages/BillingAndCollection/MeterChangeRequest/MeterChangeRequestTable';
import Reconnectiontable from '../../pages/GeneralServices/Reconnection/ReconnectionTable';
import DisconnectionTable from '../../pages/GeneralServices/Disconnection/DisconnectionTable';
import AdjustLastBillTable from '../../pages/BillingAndCollection/AdjustLastBill/AdjustLastBillTable';
import AmendBill2636Table from '../../pages/BillingAndCollection/AmendBill2636/AmendBill2636Table';
import AdvancePaymentTable from '../../pages/BillingAndCollection/AdvancePayment/AdvancePaymentTable';
import MeterReadingCorrectionTable from '../../pages/BillingAndCollection/MeterReadingCorrection/MeterReadingCorrectionTable';
import WithdrawalOfInstallmentTable from '../../pages/BillingAndCollection/WithdrawalOfInstallment/WithdrawalOfInstallmentTable';
import './SearchResultsPopup.scss';

const isAdjustLastBillApplication = (params = {}) => {
  const typeName = String(params.applicationTypeName || params.complaintType || '').toLowerCase();
  const appId = String(params.applicationId || params.eOpsTrackingId || '').toUpperCase();
  const typeId = Number(params.applicationTypeId);

  return (
    typeName.includes('adjust last bill') ||
    typeName.includes('last paid bill') ||
    appId.startsWith('BA-') ||
    typeId === 16
  );
};

const isAdvancePaymentApplication = (params = {}) => {
  const typeName = String(params.applicationTypeName || '').toLowerCase();
  const appId = String(params.applicationId || params.eOpsTrackingId || '');
  const typeId = Number(params.applicationTypeId);

  return (
    typeName.includes('advance payment') ||
    appId.startsWith('AP-') ||
    typeId === 18
  );
};

const isNewConnectionApplication = (params = {}) => {
  const typeName = String(params.applicationTypeName || params.complaintType || '').toLowerCase();
  const appId = String(params.applicationId || params.eOpsTrackingId || '').toUpperCase();
  const typeId = Number(params.applicationTypeId);

  return (
    typeName.includes('new connection') ||
    appId.startsWith('NC') ||
    typeId === 1
  );
};

const isChangeOfAttributeApplication = (params = {}) => {
  const typeName = String(params.applicationTypeName || params.complaintType || '').toLowerCase();
  const appId = String(params.applicationId || params.eOpsTrackingId || '').toUpperCase();
  const typeId = Number(params.applicationTypeId);

  return (
    typeName.includes('change of attribute') ||
    appId.startsWith('CA') ||
    typeId === 15 ||
    typeId === 3
  );
};

const isMeterReadingCorrectionApplication = (params = {}) => {
  const typeName = String(params.applicationTypeName || params.complaintType || '').toLowerCase();
  const appId = String(params.applicationId || params.eOpsTrackingId || '');
  const typeId = Number(params.applicationTypeId);

  return (
    typeName.includes('meter reading correction') ||
    appId.toUpperCase().startsWith('MRC-') ||
    typeId === 11
  );
};

const isDisconnectionApplication = (params = {}) => {
  const typeName = String(params.applicationTypeName || params.complaintType || '').toLowerCase();
  const appId = String(params.applicationId || params.eOpsTrackingId || '').toUpperCase();
  const typeId = Number(params.applicationTypeId ?? params.applicationType);

  return (
    typeName.includes('disconnection') ||
    appId.startsWith('DC-') ||
    typeId === 14 ||
    typeId === 20
  );
};

const isWithdrawalOfInstallmentApplication = (params = {}) => {
  const typeName = String(params.applicationTypeName || params.complaintType || '').toLowerCase();
  const appId = String(params.applicationId || params.eOpsTrackingId || '').toUpperCase();
  const typeId = Number(params.applicationTypeId);

  return (
    typeName.includes('withdrawal of installment') ||
    appId.startsWith('IWD-') ||
    typeId === 19
  );
};

const normalizeMrcStatusBase = (label = '') =>
  String(label)
    .replace(/\s*\(+\s*Objection\s*\)+\s*/gi, ' ')
    .replace(/\s+Objection\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim();

const getMrcStepNameFromRow = (row = {}) => {
  const progressId =
    row.applicationProgressId ??
    row.ApplicationProgressId ??
    row.progressId ??
    row.mRCProgressId ??
    row.mrcProgressId ??
    row.historyProgressId;

  // Prefer progress-id mapping so API labels that already include "(Objection)"
  // do not get duplicated in Search Results.
  const mapped = getApplicationProgressStatus(
    11,
    progressId,
    row.objectionStatus,
    '',
    row.applicationTypeName || row.complaintType || 'Meter Reading Correction',
  );

  if (mapped && mapped !== '-') {
    const base = normalizeMrcStatusBase(mapped);
    return Number(row.objectionStatus) === 1
      ? `${base} Objection`
      : base;
  }

  const apiStep = normalizeMrcStatusBase(
    row.mRCProgStatusName ||
    row.mrcProgStatusName ||
    row.progStatusName ||
    row.stepName ||
    '',
  );
  if (apiStep && apiStep !== '-' && apiStep !== '—') {
    return Number(row.objectionStatus) === 1
      ? `${apiStep} Objection`
      : apiStep;
  }

  const fallback = normalizeMrcStatusBase(row.status);
  if (fallback && fallback !== '-' && fallback !== '—') {
    return Number(row.objectionStatus) === 1 &&
      !/objection/i.test(fallback)
      ? `${fallback} Objection`
      : fallback;
  }
  return '—';
};

const resolveSearchTableType = (params = {}) => {
  const typeId = Number(params.applicationTypeId ?? params.applicationType);
  if (typeId === 2 || typeId === 5) return 2;
  if (isNewConnectionApplication(params) || typeId === 1) return 1;
  if (isChangeOfAttributeApplication(params) || typeId === 15) return 15;
  if (isAdjustLastBillApplication(params) || typeId === 16) return 16;
  if (isAdvancePaymentApplication(params) || typeId === 18) return 18;
  if (isMeterReadingCorrectionApplication(params) || typeId === 11) return 11;
  if (isWithdrawalOfInstallmentApplication(params) || typeId === 19) return 19;
  if (isDisconnectionApplication(params) || typeId === 14 || typeId === 20) return 14;
  return Number.isFinite(typeId) ? typeId : 0;
};

const getSearchResultStatusLabel = (row = {}) => {
  if (isMeterReadingCorrectionApplication(row) || Number(row.applicationTypeId) === 11) {
    return getMrcStepNameFromRow(row);
  }

  return getApplicationProgressStatus(
    isWithdrawalOfInstallmentApplication(row)
      ? 19
      : isAdvancePaymentApplication(row)
        ? 18
        : isDisconnectionApplication(row)
          ? 14
          : row.applicationTypeId,
    row.applicationProgressId ?? row.progressId,
    row.objectionStatus,
    row.status,
    row.applicationTypeName || row.complaintType,
  );
};

const STATUS_TONE_PALETTE = [
  'blue',
  'teal',
  'purple',
  'amber',
  'cyan',
  'indigo',
  'orange',
  'pink',
];

const getStatusTone = (statusLabel = '') => {
  const label = String(statusLabel).trim().toLowerCase();
  if (!label || label === '—') return 'neutral';

  if (
    label.includes('reject') ||
    label.includes('cancel') ||
    label.includes('fail') ||
    label.includes('object')
  ) {
    return 'danger';
  }
  if (
    label.includes('approv') ||
    label.includes('complet') ||
    label.includes('resolv') ||
    label.includes('closed') ||
    label.includes('success')
  ) {
    return 'success';
  }
  if (
    label.includes('pending') ||
    label.includes('wait') ||
    label.includes('hold')
  ) {
    return 'warning';
  }
  if (label.includes('verification') || label.includes('document')) {
    return 'amber';
  }
  if (label.includes('billing') || label.includes('ready')) {
    return 'teal';
  }
  if (label.includes('survey') || label.includes('estimation') || label.includes('field')) {
    return 'purple';
  }
  if (label.includes('wiring') || label.includes('test')) {
    return 'indigo';
  }
  if (label.includes('arrear') || label.includes('payment')) {
    return 'orange';
  }
  if (label.includes('meter') || label.includes('reading')) {
    return 'cyan';
  }

  let hash = 0;
  for (let i = 0; i < label.length; i += 1) {
    hash = (hash + label.charCodeAt(i) * (i + 1)) % STATUS_TONE_PALETTE.length;
  }
  return STATUS_TONE_PALETTE[hash];
};

const restoreAppOverlayIfIdle = () => {
  const openDialog = document.querySelector('.MuiDialog-container');
  if (openDialog) return false;

  document.body.classList.remove('modal-open');
  document.body.style.removeProperty('overflow');
  document.body.style.removeProperty('padding-right');
  document.documentElement.style.removeProperty('overflow');

  const root = document.getElementById('root');
  if (root) {
    root.removeAttribute('aria-hidden');
    root.removeAttribute('inert');
  }
  return true;
};

/**
 * SearchResultsPopup component displays search results in a dialog popup.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.openDialogOfApplication - Determines if the popup is visible
 * @param {Function} props.handleDialogOfApplicationClose - Callback function to close the popup
 * @param {number} props.selectedTab - The currently selected tab index
 * @param {number} props.selectedValueOfTab - The value of the selected tab
 * @param {Object[]} props.resultSearchedData - Array of primary search results
 * @param {Object[]} props.resultSearchedData1 - Array of secondary search results
 * @returns {JSX.Element|null} The search results popup component
 */
const SearchResultsPopup = ({ openDialogOfApplication, handleDialogOfApplicationClose, selectedTab, selectedValueOfTab, resultSearchedData, resultSearchedData1, setViewBackdrop }) => {
  const installmentsTableRef = useRef();
  const dueDateTableRef = useRef();
  const newConnectionTableRef = useRef();
  const changeOfLoadTableRef = useRef();
  const changeOfAttributesTableRef = useRef();
  const changeOfTariffTableRef = useRef();
  const meterChangeRequestTableRef = useRef();
  const reconnectionTableRef = useRef();
  const disconnectionTableRef = useRef();
  const adjustLastBillTableRef = useRef();
  const partPaymentTableRef = useRef();
  const advancePaymentTableRef = useRef();
  const meterReadingCorrectionTableRef = useRef();
  const withdrawalOfInstallmentTableRef = useRef();
  const [applicantName, setApplicantName] = useState('');
  const [rowPatches, setRowPatches] = useState({});

  const hasHandledRowClick = useRef(false);
  const openedSingleResultKey = useRef('');
  // Keep opened service tables mounted so closing this dialog does not tear down a workflow popup.
  const [mountedTableTypes, setMountedTableTypes] = useState(() => new Set());

  const searchedRows = useMemo(() => {
    const data1 = Array.isArray(resultSearchedData) ? resultSearchedData : [];
    const data2 = Array.isArray(resultSearchedData1) ? resultSearchedData1 : [];
    return data1.concat(data2);
  }, [resultSearchedData, resultSearchedData1]);

  const mergeState = useMemo(() => (
    searchedRows.map((row) => {
      const id = row.applicationId || row.eOpsTrackingId;
      const patch = id ? rowPatches[id] : null;
      return patch ? { ...row, ...patch } : row;
    })
  ), [searchedRows, rowPatches]);

  const mountTableType = useCallback((typeId) => {
    const id = Number(typeId);
    if (!id) return;
    setMountedTableTypes((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const waitForTableRef = async (tableRef, methodName = 'handleRowClick', maxAttempts = 30) => {
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (typeof tableRef.current?.[methodName] === 'function') {
        return tableRef.current;
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return null;
  };

  const openApplicationFromSearch = useCallback(async (params) => {
    const tableType = resolveSearchTableType(params);
    const typeId = Number(params.applicationTypeId);
    mountTableType(tableType);

    if (tableType === 1) {
      const table = await waitForTableRef(newConnectionTableRef);
      if (!table) throw new Error('New Connection table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 2) {
      const table = await waitForTableRef(meterChangeRequestTableRef);
      if (!table) throw new Error('Meter Change Request table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 15) {
      const table = await waitForTableRef(changeOfAttributesTableRef);
      if (!table) throw new Error('Change of Attributes table not ready');
      return table.handleRowClick(params.applicationId);
    }
    if (tableType === 4) {
      const table = await waitForTableRef(changeOfTariffTableRef);
      if (!table) throw new Error('Change of Tariff table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 6) {
      const table = await waitForTableRef(changeOfLoadTableRef);
      if (!table) throw new Error('Change of Load table not ready');
      return table.handleRowClick(params.applicationId);
    }
    if (tableType === 7) {
      const table = await waitForTableRef(dueDateTableRef);
      if (!table) throw new Error('Due Date Extension table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 8) {
      const table = await waitForTableRef(installmentsTableRef);
      if (!table) throw new Error('Installments table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 13) {
      const table = await waitForTableRef(reconnectionTableRef);
      if (!table) throw new Error('Reconnection table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 14) {
      const table = await waitForTableRef(disconnectionTableRef);
      if (!table) throw new Error('Disconnection table not ready');
      return table.handleRowClick(params.applicationId || params.eOpsTrackingId, params);
    }
    if (tableType === 16) {
      const table = await waitForTableRef(adjustLastBillTableRef);
      if (!table) throw new Error('Adjust Last Bill table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 18) {
      const table = await waitForTableRef(advancePaymentTableRef);
      if (!table) throw new Error('Advance Payment table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 11) {
      const table = await waitForTableRef(meterReadingCorrectionTableRef);
      if (!table) throw new Error('Meter Reading Correction table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (typeId === 17 || tableType === 17) {
      const table = await waitForTableRef(partPaymentTableRef);
      if (!table) throw new Error('Part Payment table not ready');
      return table.handleRowClick(params.applicationId, params.status);
    }
    if (tableType === 19) {
      const table = await waitForTableRef(withdrawalOfInstallmentTableRef);
      if (!table) throw new Error('Withdrawal of Installment table not ready');
      return table.handleRowClick(params.applicationId, params);
    }

    return null;
  }, [mountTableType]);

  useEffect(() => {
    setRowPatches((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [searchedRows]);

  useEffect(() => {
    if (!openDialogOfApplication) {
      hasHandledRowClick.current = false;
      openedSingleResultKey.current = '';
      setApplicantName((prev) => (prev === '' ? prev : ''));
      return;
    }

    const namedRow = mergeState.find((item) => item.applicantName || item.name);
    const nextName = namedRow?.applicantName || namedRow?.name || '';
    setApplicantName((prev) => (prev === nextName ? prev : nextName));

    if (mergeState.length !== 1 || hasHandledRowClick.current) {
      return;
    }

    const single = mergeState[0];
    const singleKey = `${single?.applicationTypeId || ''}:${single?.applicationId || ''}`;
    if (!single?.applicationId || openedSingleResultKey.current === singleKey) {
      return;
    }

    hasHandledRowClick.current = true;
    openedSingleResultKey.current = singleKey;
    openApplicationFromSearch(single).catch(() => {
      hasHandledRowClick.current = false;
      openedSingleResultKey.current = '';
    });
  }, [
    openDialogOfApplication,
    mergeState,
    mountTableType,
    openApplicationFromSearch,
  ]);

  useEffect(() => {
    if (openDialogOfApplication) return undefined;

    setViewBackdrop?.(false);

    const timer = window.setTimeout(() => {
      if (!restoreAppOverlayIfIdle()) return;
      setMountedTableTypes((prev) => (prev.size === 0 ? prev : new Set()));
    }, 400);

    return () => window.clearTimeout(timer);
  }, [openDialogOfApplication, setViewBackdrop]);

  /**
   * Handles row click event and triggers the appropriate table method.
   *
   * @param {Object} params - The selected row data
   * @param {number} params.applicationTypeId - ID representing the application type
   * @param {number} params.applicationId - ID of the selected application
   */


  //     try {
  //         const myHeaders = new Headers();
  //         myHeaders.append("Authorization", token);
  //         myHeaders.append("Content-Type", "application/json");

  //         const raw = JSON.stringify({
  //             // subDivisionCode: dialogRowData.applicationId.slice(3, 8)
  //             // subDivisionCode: '15175'
  //             subDivisionCode: applicationId.slice(3, 8)

  //         });

  //         const requestOptions = {
  //             method: "POST",
  //             headers: myHeaders,
  //             body: raw,
  //             redirect: "follow",
  //         };

  //         const response = await fetch(cfpBaseUrl + "enc/getMaterialDetails", requestOptions);
  //         const result = await response.json();
  //         setAllMaterialList(result.data);

  //         return result.data;
  //     } catch (e) {
  //         return [];
  //     }
  // };



  const handleRowClick = async (params) => {
    setViewBackdrop(true);
    try {
      await openApplicationFromSearch(params);
    } catch (error) {
    } finally {
      setViewBackdrop(false);
    }
  };

  const downloadEFileFromSearch = useCallback(async (row) => {
    const applicationId = row?.applicationId || row?.eOpsTrackingId;
    if (!applicationId) return;

    setViewBackdrop(true);
    try {
      if (isNewConnectionApplication(row)) {
        mountTableType(1);
        const table = await waitForTableRef(
          newConnectionTableRef,
          'downloadEFileFromSearch',
        );
        if (!table) throw new Error('New Connection table not ready');
        await table.downloadEFileFromSearch(applicationId);
        return;
      }

      if (isChangeOfAttributeApplication(row)) {
        mountTableType(15);
        const table = await waitForTableRef(
          changeOfAttributesTableRef,
          'downloadEFileFromSearch',
        );
        if (!table) throw new Error('Change of Attributes table not ready');
        await table.downloadEFileFromSearch(applicationId);
      }
    } catch (error) {
      // Search results stay visible if E-File cannot open.
    } finally {
      setViewBackdrop(false);
    }
  }, [mountTableType, setViewBackdrop]);

  const downloadSCOFromSearch = useCallback(async (row) => {
    const applicationId = row?.applicationId || row?.eOpsTrackingId;
    if (!applicationId || !isNewConnectionApplication(row)) return;

    setViewBackdrop(true);
    try {
      mountTableType(1);
      const table = await waitForTableRef(
        newConnectionTableRef,
        'downloadSCOFromSearch',
      );
      if (!table) throw new Error('New Connection table not ready');
      await table.downloadSCOFromSearch(applicationId);
    } catch (error) {
      // Search results stay visible if SCO cannot open.
    } finally {
      setViewBackdrop(false);
    }
  }, [mountTableType, setViewBackdrop]);

  const handleSearchResultUpdated = useCallback((updates = {}) => {
    const applicationId = updates.applicationId;
    if (!applicationId) return;

    const nextApplicantName = updates.applicantName || updates.name || "";

    setRowPatches((prev) => ({
      ...prev,
      [applicationId]: {
        ...(prev[applicationId] || {}),
        ...updates,
        applicantName: nextApplicantName || updates.applicantName,
        name: nextApplicantName || updates.name,
        applicantCNIC: updates.applicantCNIC,
        cnic: updates.applicantCNIC,
      },
    }));

    if (nextApplicantName) {
      setApplicantName((prev) => (prev === nextApplicantName ? prev : nextApplicantName));
    }
  }, []);

  const searchTypeLabel = useMemo(() => {
    if (selectedTab === 0) return 'Reference No';
    if (selectedTab === 1) return 'Application ID';
    if (selectedTab === 2) return 'CNIC';
    if (selectedTab === 3) return 'Name';
    return 'Search';
  }, [selectedTab]);

  const resultCount = mergeState.length;
  const canDownloadSearchEFile = (row) =>
    isNewConnectionApplication(row) || isChangeOfAttributeApplication(row);

  const canDownloadSearchSco = (row) => {
    if (!isNewConnectionApplication(row)) return false;
    const progress = Number(
      row.applicationProgressId
        ?? row.progressId
        ?? row.ApplicationProgressId
        ?? row.applicationProgressNumber,
    );
    // Ready for Billing (9) and Completed (10) — SCO is created after Meter Installation.
    if (progress === 9 || progress === 10) return true;
    const status = [
      getSearchResultStatusLabel(row),
      row.status,
      row.applicationStatus,
      row.applicationStatusId,
      row.progStatusName,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .join(' ');
    return status.includes('ready for billing') || status.includes('complet');
  };

  const typeCounts = useMemo(() => {
    const counts = new Map();
    mergeState.forEach((row) => {
      const typeLabel = (
        row.applicationTypeName ||
        row.complaintType ||
        'Other'
      ).trim();
      counts.set(typeLabel, (counts.get(typeLabel) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count || a.type.localeCompare(b.type));
  }, [mergeState]);

  return (
    <>
      <Dialog
        open={openDialogOfApplication}
        onClose={handleDialogOfApplicationClose}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
        disableScrollLock
        className="search-results-popup"
      >
        <div className="search-results-popup__header">
          <div className="search-results-popup__header-left">
            <span className="search-results-popup__header-icon" aria-hidden>
              <SearchOutlinedIcon />
            </span>
            <div className="search-results-popup__header-text">
              <h2 className="search-results-popup__title">Search Results</h2>
              <p className="search-results-popup__subtitle">
                {searchTypeLabel}: {selectedValueOfTab || '—'}
                {selectedTab !== 3 && applicantName ? ` · ${applicantName}` : ''}
              </p>
            </div>
          </div>
          <IconButton
            onClick={handleDialogOfApplicationClose}
            className="search-results-popup__close"
            aria-label="Close search results"
          >
            <CloseIcon />
          </IconButton>
        </div>

        {resultCount > 0 ? (
          <DialogContent className="search-results-popup__content">
            <div className="search-results-popup__toolbar">
              <div className="search-results-popup__type-counts" role="list">
                {typeCounts.map(({ type, count }) => (
                  <span
                    key={type}
                    className="search-results-popup__type-chip"
                    role="listitem"
                    title={`${type}: ${count}`}
                  >
                    <span className="search-results-popup__type-chip-name">
                      {type}
                    </span>
                    <span className="search-results-popup__type-chip-count">
                      {count}
                    </span>
                  </span>
                ))}
                <span className="search-results-popup__count">
                  {resultCount} record{resultCount === 1 ? '' : 's'} found
                </span>
              </div>
            </div>
            <div className="search-results-popup__table-wrap">
              <div className="search-results-popup__table-head">
                <Table className="search-results-popup__table">
                  <TableHead>
                    <TableRow>
                      {selectedTab !== 1 && (
                        <TableCell>Application / Ticket No.</TableCell>
                      )}
                      <TableCell>Date</TableCell>
                      {selectedTab !== 2 && (
                        <TableCell>Applicant CNIC</TableCell>
                      )}
                      {selectedTab !== 0 && (
                        <TableCell>Tracking ID</TableCell>
                      )}
                      {selectedTab !== 3 && (
                        <TableCell>Applicant Name</TableCell>
                      )}
                      <TableCell>Type</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell className="search-results-popup__actions">
                        E-File / SCO
                      </TableCell>
                    </TableRow>
                  </TableHead>
                </Table>
              </div>
              <div className="search-results-popup__table-body">
                <Table className="search-results-popup__table">
                  <TableBody>
                    {mergeState.map((row, index) => {
                      const statusLabel = getSearchResultStatusLabel(row);
                      const trackingId = row.applicationId || '—';
                      const appOrTicket =
                        row.eOpsTrackingId || row.applicationId || '—';

                      return (
                        <TableRow
                          key={`${trackingId}-${index}`}
                          onClick={() => handleRowClick(row)}
                        >
                          {selectedTab !== 1 && (
                            <TableCell title={appOrTicket}>
                              {appOrTicket}
                            </TableCell>
                          )}
                          <TableCell>
                            {(row.creationDate && row.creationDate) ||
                              (row.registeredAt &&
                                convertDatewithTime(row.registeredAt)) ||
                              '—'}
                          </TableCell>
                          {selectedTab !== 2 && (
                            <TableCell>
                              {row.cnic || row.applicantCNIC
                                ? row.cnic || row.applicantCNIC
                                : '—'}
                            </TableCell>
                          )}
                          {selectedTab !== 0 && (
                            <TableCell
                              className="search-results-popup__tracking"
                              title={trackingId}
                            >
                              {trackingId}
                            </TableCell>
                          )}
                          {selectedTab !== 3 && (
                            <TableCell
                              title={
                                row.name || row.applicantName || ''
                              }
                            >
                              {row.name || row.applicantName
                                ? row.name || row.applicantName
                                : '—'}
                            </TableCell>
                          )}
                          <TableCell>
                            {row.applicationTypeName ||
                              row.complaintType ||
                              '—'}
                          </TableCell>
                          <TableCell>
                            {row.submittedThrough
                              ? row.submittedThrough
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`search-results-popup__status search-results-popup__status--${getStatusTone(statusLabel)}`}
                              title={statusLabel || ''}
                            >
                              {statusLabel || '—'}
                            </span>
                          </TableCell>
                          <TableCell
                            className="search-results-popup__actions"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {canDownloadSearchEFile(row) || canDownloadSearchSco(row) ? (
                              <div className="search-results-popup__action-btns">
                                {canDownloadSearchEFile(row) ? (
                                  <Tooltip title="Download E-File" arrow>
                                    <IconButton
                                      size="small"
                                      className="search-results-popup__download-btn"
                                      onClick={() =>
                                        downloadEFileFromSearch(row)
                                      }
                                      aria-label="Download E-File"
                                    >
                                      <FileDownloadOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : null}
                                {canDownloadSearchSco(row) ? (
                                  <Tooltip title="Download SCO" arrow>
                                    <button
                                      type="button"
                                      className="search-results-popup__sco-btn"
                                      onClick={() => downloadSCOFromSearch(row)}
                                      aria-label="Download SCO"
                                    >
                                      SCO
                                    </button>
                                  </Tooltip>
                                ) : null}
                              </div>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </DialogContent>
        ) : (
          <Box className="search-results-popup__empty">
            <span className="search-results-popup__empty-icon" aria-hidden>
              <InboxOutlinedIcon />
            </span>
            <p className="search-results-popup__empty-title">No results found</p>
            <p className="search-results-popup__empty-text">
              No application matched this search. Try another ID, CNIC, or name.
            </p>
          </Box>
        )}
      </Dialog>

      {mountedTableTypes.has(8) && (
        <InstallmentsTable ref={installmentsTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(7) && (
        <DueDateExtensionTable ref={dueDateTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(1) && (
        <Newconnectiontable ref={newConnectionTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(6) && (
        <ChangeLoadTable ref={changeOfLoadTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(15) && (
        <ChangeAttributesTable
          ref={changeOfAttributesTableRef}
          showTable={false}
          onCloseSearchResults={handleDialogOfApplicationClose}
          onSearchResultUpdated={handleSearchResultUpdated}
        />
      )}
      {mountedTableTypes.has(4) && (
        <ChangeTariffTable ref={changeOfTariffTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(2) && (
        <MeterChangeRequestTable ref={meterChangeRequestTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(13) && (
        <Reconnectiontable ref={reconnectionTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(14) && (
        <DisconnectionTable ref={disconnectionTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(16) && (
        <AdjustLastBillTable ref={adjustLastBillTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(17) && (
        <AmendBill2636Table ref={partPaymentTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(18) && (
        <AdvancePaymentTable ref={advancePaymentTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(11) && (
        <MeterReadingCorrectionTable ref={meterReadingCorrectionTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
      {mountedTableTypes.has(19) && (
        <WithdrawalOfInstallmentTable ref={withdrawalOfInstallmentTableRef} showTable={false} onCloseSearchResults={handleDialogOfApplicationClose} />
      )}
    </>
  )
}

export default SearchResultsPopup
